import { supabase } from './supabase';
import { UserProfile, UserTier } from '../types';

/**
 * Authentication service for user sign up, sign in, and profile management
 */
export class AuthService {
  /**
   * Sign up a new user with email and password
   */
  async signUp(email: string, password: string): Promise<{ error?: Error; user?: any }> {
    try {
      const currentPath = window.location.pathname;
      console.log('🔐 [AuthService] Starting email signup...');
      console.log('🔐 [AuthService] Current path:', currentPath);
      
      // Redirect back to the current URL (including space ID) with confirmation parameter
      const redirectUrl = `${window.location.origin}${currentPath}?confirmed=true`;
      console.log('🔐 [AuthService] Redirect URL:', redirectUrl);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });

      if (error) throw error;
      return { user: data.user };
    } catch (error) {
      return { error: error as Error };
    }
  }

  /**
   * Sign in existing user with email and password
   */
  async signIn(email: string, password: string): Promise<{ error?: Error; user?: any }> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      return { user: data.user };
    } catch (error) {
      return { error: error as Error };
    }
  }

  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle(): Promise<{ error?: Error }> {
    try {
      const currentPath = window.location.pathname;
      console.log('🔐 [AuthService] Starting Google OAuth...');
      console.log('🔐 [AuthService] Current path:', currentPath);
      
      // Redirect back to the current URL (including space ID)
      const redirectUrl = `${window.location.origin}${currentPath}`;
      console.log('🔐 [AuthService] Redirect URL:', redirectUrl);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        },
      });

      if (error) throw error;
      return {};
    } catch (error) {
      console.error('🔐 [AuthService] Google OAuth error:', error);
      return { error: error as Error };
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<{ error?: Error }> {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return {};
    } catch (error) {
      return { error: error as Error };
    }
  }

  /**
   * Get current authenticated user
   * Uses getSession() which processes OAuth callback hash fragments
   */
  async getCurrentUser() {
    console.log('🔐 [AuthService] Getting session...');
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    
    console.log('🔐 [AuthService] Session:', session ? 'found' : 'null');
    console.log('🔐 [AuthService] Error:', error);
    
    if (session) {
      console.log('🔐 [AuthService] User:', session.user?.email);
      console.log('🔐 [AuthService] Access token present:', !!session.access_token);
    }
    
    return session?.user || null;
  }

  /**
   * Get user profile from database
   */
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data as UserProfile;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }

  /**
   * Update user subscription tier
   */
  async updateUserTier(
    userId: string,
    tier: UserTier,
    subscriptionId?: string
  ): Promise<{ error?: Error }> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          tier,
          subscription_id: subscriptionId || null,
        })
        .eq('id', userId);

      if (error) throw error;
      return {};
    } catch (error) {
      return { error: error as Error };
    }
  }

  /**
   * Check if user has premium access
   */
  async isPremiumUser(userId: string): Promise<boolean> {
    try {
      const profile = await this.getUserProfile(userId);
      return profile?.has_premium || false;
    } catch (error) {
      console.error('Error checking premium status:', error);
      return false;
    }
  }

  /**
   * Listen for authentication state changes
   */
  onAuthStateChange(callback: (user: any) => void) {
    return supabase.auth.onAuthStateChange((_event, session) => {
      callback(session?.user || null);
    });
  }
}

export const authService = new AuthService();
